"use client";

import React, { useRef } from "react";
import { useGSAP } from "@gsap/react";
import hasFadeAnim from "@/lib/animation/hasFadeAnim";
import hasTextMovAnim from "@/lib/animation/hasTextMovAnim";
import gsap from "gsap";

interface ServiceDetails {
  title: string;
  items: string[];
}
interface ServiceData {
  title: string;
  services: ServiceDetails[];
}
interface Props {
  services: ServiceData;
}

const CreativeServices = ({ services }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null!);

  useGSAP(
    () => {
      hasFadeAnim();
      hasTextMovAnim();

      const cards = containerRef.current.querySelectorAll(".service-card");

      cards.forEach((card, cardIdx) => {
        const items = card.querySelectorAll(".service-item");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        });

        // Animate the card container
        tl.to(card, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
        });

        // Stagger the items inside the card
        tl.to(
          items,
          {
            opacity: 1,
            x: 0,
            duration: 0.5,
            stagger: 0.1,
            ease: "power2.out",
          },
          "-=0.4",
        );
      });
    },
    { scope: containerRef },
  );
  return (
    <section
      className="bg-white text-black w-full section-spacing-top"
      ref={containerRef}
    >
      <div className="mb-10 md:mb-16">
        <h2 className="text-center text-[28px] md:text-[34px] lg:text-[40px] font-bold leading-tight tracking-tight has_text_mov_anim">
          {services?.title}
        </h2>
      </div>

      <div className="w-full mx-auto px-4 md:px-8">
        <div className="flex flex-wrap justify-center gap-y-6 md:gap-y-8">
          {services?.services?.map((service, idx) => {
            return (
              <div
                key={idx}
                className={`service-card opacity-0 translate-y-8 w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/6 flex flex-col px-4 md:px-6 lg:px-8 py-4 md:py-6 text-left border-black/10 border-r-0
                `}
              >
                <h3 className="text-[20px] sm:text-[22px] lg:text-[24px] font-normal leading-tight mb-4 md:mb-6 whitespace-pre-line text-black">
                  {service.title}
                </h3>
                <ul className="flex flex-col gap-2">
                  {service.items.map((item, i) => (
                    <li
                      key={i}
                      className="service-item opacity-0 -translate-x-4 flex items-start text-[13px] md:text-[14px] font-light text-black/80 leading-snug"
                    >
                      <div className="flex items-center h-[1.2rem] mr-2 md:mr-4 flex-shrink-0">
                        <div className="w-[4px] h-[4px] rounded-full bg-black/60" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CreativeServices;
